import { MarvColor, MarvColorPrimary, MarvColorBlue, MarvColorGreen, MarvColorOrange, MarvColorRed } from '@marv/types';
import { MarvStringService } from '@marv/services';
import { Address, MissionStatus } from "@app/models";
import { RetailerLogo } from '@app/components';
import moment from 'moment';

export class PearlsPlanningEntry {

    planningEntryType: string;

    // pearls context id
    ctxId: number;
    // storecheck or event Id
    id: number;

    logaDay: number;
    areaId: number;
    areaName: string;
    sectorId: number;
    sectorName: string;

    storeId: number;
    storeName: string;
    storeCode: string;
    description: string;

    salesRepId: number;
    salesRepName: string

    startTime: string;
    endTime: string;

    status: MissionStatus;
    statusClass: string;

    retailerId: number;
    retailerName: string;
    retailerCode: string;
    retailerIconUrl: string;
    nbPictures: number;

    objectives: Objective[];

    address: Address;
    checkInDone: boolean;
    hrDuration: string;

    /**
     * utitility flag to show/hide content with simple filtering
     */
    uiShouldHide: boolean;

    static FromJson(json: any): PearlsPlanningEntry {
        let pearlsPlanning: PearlsPlanningEntry = new PearlsPlanningEntry();

        pearlsPlanning.planningEntryType = json['planningEntryType'];

        pearlsPlanning.ctxId = json['ctxId'];
        pearlsPlanning.id = json['id'];
        pearlsPlanning.logaDay = json['logaDay'];
        pearlsPlanning.areaId = json['areaId'];
        pearlsPlanning.areaName = json['areaName'];
        pearlsPlanning.sectorId = json['sectorId'];
        pearlsPlanning.sectorName = json['sectorName'];
        pearlsPlanning.storeId = json['storeId'];
        pearlsPlanning.storeName = json['storeName'];
        pearlsPlanning.storeCode = json['storeCode'];
        pearlsPlanning.salesRepId = json['salesRepId'];
        pearlsPlanning.salesRepName = json['salesRepName'];
        pearlsPlanning.status = MissionStatus.FromPearlsPlanningEntry(json['status'], json['logaDay']);
        pearlsPlanning.statusClass = json['statusClass'];
        pearlsPlanning.nbPictures = json['nbPictures'];
        pearlsPlanning.retailerId = json['retailerId'];
        pearlsPlanning.retailerName = json['retailerName'];
        pearlsPlanning.retailerCode = json['retailerCode'];
        pearlsPlanning.startTime = json['startTime'];
        pearlsPlanning.endTime = json['endTime'];
        pearlsPlanning.retailerIconUrl = json['retailerIconUrl'];
        pearlsPlanning.address = json['address'];
        pearlsPlanning.checkInDone = json['checkInDone'];
        pearlsPlanning.hrDuration = json['hrDuration'];
        pearlsPlanning.description = json['description'];
        pearlsPlanning.objectives = [];

        let objectives = json['objectives']
        if (objectives)
          objectives.forEach( (oobj: any) => pearlsPlanning.objectives.push(Objective.FromJson(oobj)));


        return pearlsPlanning;
    }
}

export class Objective {
    id: number;
    code: string;
    name: string;
    fulfilled: boolean;

    static FromJson(o: Objective) {
        let obj: Objective = new Objective();
        obj.id = o.id;
        obj.name = o.name;
        obj.code = o.code;
        obj.fulfilled = o.fulfilled;
        return obj;
    }
}

export class RetailerPlanning {
    id: number;
    name: string;
    logo: RetailerLogo;
    code: string;
    normalizedName: string;
}

export class StoreSectorPlanning {
    id: number;
    code: string;
    name: string;
    normalizedName: string;
}

export class StoreRankingPlanning {
    id: number;
    code: string;
    name: string;
    normalizedName: string;
}

export class StoreProfilPlanning {
    id: number;
    code: string;
    name: string;
    normalizedName: string;
}

export class StorePlanning {
    
    id: number;
    code: string;
    name: string;
    normalizedName: string;
    address: string;
    retailer: RetailerPlanning;    
    sector: StoreSectorPlanning;
    ranking: StoreRankingPlanning;
    profile: StoreProfilPlanning;
    lastVisitDate: string;
    
    static FromJson(o: any): StorePlanning{
        
        let r = new RetailerPlanning();
        r.id = o.retailerDto?.id;
        r.code = o.retailerDto?.code;
        r.name = o.retailerDto?.name;
        r.normalizedName = MarvStringService.RemoveAccents(o.retailerDto?.name);
        r.logo = new RetailerLogo(o.retailerDto?.name);
        
        let sc = new StoreSectorPlanning();
        sc.id = o.sectorDto?.id;
        sc.code = o.sectorDto?.code;
        sc.name = o.sectorDto?.name;
        sc.normalizedName = MarvStringService.RemoveAccents(o.sectorDto?.name);
        
        let rk = new StoreRankingPlanning();
        rk.id = o.storeRankingDto?.id;
        rk.code = o.storeRankingDto?.code;
        rk.name = o.storeRankingDto?.name;
        rk.normalizedName = MarvStringService.RemoveAccents(o.storeRankingDto?.name);
        
        let p = new StoreProfilPlanning();
        p.id = o.storeProfileDto?.id;
        p.code = o.storeProfileDto?.code;
        p.name = o.storeProfileDto?.name;
        p.normalizedName = MarvStringService.RemoveAccents(o.storeProfileDto?.name);
        
        let s = new StorePlanning();
        s.id = o.id;
        s.code = o.code;
        s.name = o.name;
        s.normalizedName = MarvStringService.RemoveAccents(o.name);
        s.address = o.address;
        s.retailer = r;
        s.sector = sc;
        s.ranking = rk;
        s.profile = p;
        s.lastVisitDate = o.lastVisitDate;
        return s;
    }
}

export class EventPlanningObjectives {
    name: string;
}

export class EventPlanningExtendedProp {
    plannedObjectives: Array<EventPlanningObjectives>;
    planningEvent: any;
    retailerLogo: RetailerLogo;
    storeName: string;
    workerName: string;
    eventType: string;
}

export class EventPlanning {
    id: number;
    title: string;
    start: Date;
    end: Date;
    className: string;
    allowToCancel: boolean;
    allowToDelete: boolean;
    allowToDuplicate: boolean;
    allowToUpdate: boolean;
    extendedProp: EventPlanningExtendedProp;
    
    uiTop: number = 0;
    uiLeft: number = 0;
    uiWidth: number = 100;
    uiHeight: number = 25;
    uiHeightInitial: number = 25;
    
    static FromJson(o: any): EventPlanning{
        
        let p = new EventPlanningExtendedProp();        
        p.eventType = o.eventType;
        p.plannedObjectives = o.plannedObjectives;
        p.planningEvent = o.planningEvent;
        p.workerName = o.workerName;
        if(o.eventType == 'storeCheck'){            
            p.retailerLogo = new RetailerLogo(o.retailerName);
            p.storeName = o.storeName;
        }
        
        let e = new EventPlanning();
        e.id = o.id;
        e.title = o.title;
        e.start = new Date(o.start);
        e.end = new Date(o.end);
        e.className = o.className;
        e.allowToCancel = o.allowToCancel;
        e.allowToDelete = o.allowToDelete;
        e.allowToDuplicate = o.allowToDuplicate;
        e.allowToUpdate = o.allowToUpdate;
        e.extendedProp = p;
        return e;
    }
}

